import { JSX, useEffect } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { NormalizedLoad, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

export type CustomerSectionFormProps = {
  formMethods: UseFormReturn<NormalizedLoad>;
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  isLoadingCustomers: boolean;
  setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
  originalSuggestionData?: NormalizedLoad;
};

export function CustomerSectionForm({
  formMethods,
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  originalSuggestionData,
}: CustomerSectionFormProps): JSX.Element {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = formMethods;
  const { tmsIntegrations } = useServiceFeatures();

  const watchedCustomer = watch('customer');
  const watchedAddress2 = watch('customer.addressLine2');

  const hasCustomerInfo =
    !!watchedCustomer?.addressLine1 ||
    !!watchedAddress2 ||
    !!watchedCustomer?.city ||
    !!watchedCustomer?.state ||
    !!watchedCustomer?.zipCode;

  useEffect(() => {
    if (watchedCustomer?.externalTMSID) {
      const selectedCustomer = customers?.find(
        (c) => c.externalTMSID === watchedCustomer?.externalTMSID
      );
      if (!selectedCustomer) {
        return;
      }

      Object.entries(selectedCustomer).forEach(([key, value]) => {
        setValue(`customer.${key}` as FieldPath<NormalizedLoad>, value, {
          shouldDirty: true,
        });
      });
    }
  }, [watchedCustomer?.externalTMSID, customers, setValue]);

  return (
    <>
      <RHFCustomerSelect
        required
        name='customer.externalTMSID'
        label='Name'
        control={control}
        errors={errors}
        customers={customers}
        setCustomers={setCustomers}
        isLoadingCustomers={isLoadingCustomers}
        setIsLoadingCustomers={setIsLoadingCustomers}
        tmsID={tmsIntegrations?.[0]?.id}
      />

      {watchedCustomer && (
        <>
          {hasCustomerInfo && (
            <>
              <div className='text-sm text-neutral-500 mb-1'>
                Customer Information
              </div>
              <div className='space-y-2 text-neutral-700'>
                {watchedCustomer.addressLine1 && (
                  <div className='text-sm'>
                    <span className='font-medium'>Address:</span>{' '}
                    {watchedCustomer.addressLine1}
                  </div>
                )}
                {watchedAddress2 && (
                  <div className='text-sm'>
                    <span className='font-medium'>Address Line 2:</span>{' '}
                    {watchedAddress2}
                  </div>
                )}
                {watchedCustomer.city && (
                  <div className='text-sm'>
                    <span className='font-medium'>City:</span>{' '}
                    {watchedCustomer.city}
                  </div>
                )}
                {watchedCustomer.state && (
                  <div className='text-sm'>
                    <span className='font-medium'>State:</span>{' '}
                    {watchedCustomer.state}
                  </div>
                )}
                {watchedCustomer.zipCode && (
                  <div className='text-sm'>
                    <span className='font-medium'>Zip Code:</span>{' '}
                    {watchedCustomer.zipCode}
                  </div>
                )}
              </div>
            </>
          )}

          <LoadRefPillInput
            name='customer.refNumber'
            label='Ref #/ BOL'
            control={control}
            originalSuggestionData={originalSuggestionData || getValues()}
            autoSelectFirstOption={false}
          />

          <LoadRefPillInput
            name='poNums'
            label='PO #'
            control={control}
            originalSuggestionData={originalSuggestionData}
            autoSelectFirstOption={false}
          />
        </>
      )}
    </>
  );
}
